/**
 * Página: Activación de cuenta (pública, sin login ni sidebar)
 * Se llega acá desde el link del mail de invitación. Valida el token al
 * cargar, y si es válido deja elegir la contraseña (con confirmación).
 * Misma identidad visual que Login.jsx — es la otra mitad del mismo flujo.
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  STROKE_DEGRADE,
  IconContrasena,
  IconMostrarContrasena,
  IconOcultarContrasena,
} from '../components/icons';
import '../styles/login.css';

const API_URL = process.env.REACT_APP_API_URL;

const ActivarCuenta = () => {
  const { token } = useParams();

  // 'validando' | 'valido' | 'invalido' | 'activada'
  const [estado, setEstado] = useState('validando');
  const [datosInvitacion, setDatosInvitacion] = useState(null);
  const [errorValidacion, setErrorValidacion] = useState('');
  // Una invitación demo vencida (invitados de tumunicipio) responde con este codigo:
  // el mensaje del backend ya dice a quién escribir, así que no se le suma el texto
  // genérico de "pedile al administrador de tu municipio", que no le corresponde.
  const [esDemoVencida, setEsDemoVencida] = useState(false);

  const [contrasena, setContrasena] = useState('');
  const [confirmarContrasena, setConfirmarContrasena] = useState('');
  const [mostrarContrasena, setMostrarContrasena] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errorFormulario, setErrorFormulario] = useState('');

  useEffect(() => {
    const validar = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/activar-cuenta/${token}`);
        setDatosInvitacion(data);
        setEstado('valido');
      } catch (err) {
        console.error('Error al validar el link de activación:', err);
        setErrorValidacion(err.response?.data?.mensaje || 'El link de activación no es válido');
        setEsDemoVencida(err.response?.data?.codigo === 'DEMO_VENCIDA');
        setEstado('invalido');
      }
    };
    validar();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorFormulario('');

    if (contrasena.length < 8) {
      setErrorFormulario('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (contrasena !== confirmarContrasena) {
      setErrorFormulario('Las contraseñas no coinciden');
      return;
    }

    try {
      setEnviando(true);
      await axios.post(`${API_URL}/activar-cuenta/${token}`, { contrasena });
      setEstado('activada');
    } catch (err) {
      console.error('Error al activar la cuenta:', err);
      setErrorFormulario(err.response?.data?.mensaje || 'Error al activar la cuenta');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-gradient"></div>

      <div className="login-card">
        <div className="login-logo">
          <img src="/logo-betech.svg" alt="Betech" className="logo-img" />
        </div>

        {estado === 'validando' && (
          <>
            <h1 className="login-title">Activar cuenta</h1>
            <p className="login-subtitle">Un momento, estamos validando tu link...</p>
          </>
        )}

        {estado === 'invalido' && (
          <>
            <h1 className="login-title">Activar cuenta</h1>
            <div className="error-message">{errorValidacion}</div>
            {!esDemoVencida && (
              <p className="login-subtitle" style={{ marginTop: 16 }}>
                Pedile al administrador de tu municipio que te reenvíe la invitación, o iniciá sesión si ya tenés una cuenta activa.
              </p>
            )}
            <div className="login-links" style={{ marginTop: 12 }}>
              <Link to="/login" className="link-ayuda">Ir a iniciar sesión</Link>
            </div>
          </>
        )}

        {estado === 'valido' && (
          <>
            <h1 className="login-title">Activar cuenta</h1>
            <p className="login-subtitle">
              Hola {datosInvitacion.nombre_completo}, elegí tu contraseña para {datosInvitacion.email}.
            </p>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="contrasena">Contraseña</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconContrasena size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="contrasena"
                    type={mostrarContrasena ? 'text' : 'password'}
                    placeholder="Al menos 8 caracteres"
                    value={contrasena}
                    onChange={(e) => setContrasena(e.target.value)}
                    className="form-input"
                    disabled={enviando}
                    required
                  />
                  <button
                    type="button"
                    className="btn-mostrar-contraseña"
                    onClick={() => setMostrarContrasena(!mostrarContrasena)}
                    tabIndex="-1"
                  >
                    {mostrarContrasena
                      ? <IconOcultarContrasena size={16} stroke="#555" />
                      : <IconMostrarContrasena size={16} stroke="#555" />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmar-contrasena">Confirmar contraseña</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconContrasena size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="confirmar-contrasena"
                    type={mostrarContrasena ? 'text' : 'password'}
                    placeholder="Repetí la contraseña"
                    value={confirmarContrasena}
                    onChange={(e) => setConfirmarContrasena(e.target.value)}
                    className="form-input"
                    disabled={enviando}
                    required
                  />
                </div>
              </div>

              {errorFormulario && <div className="error-message">{errorFormulario}</div>}

              <button type="submit" className="btn-ingresar" disabled={enviando}>
                {enviando ? 'Activando...' : 'Activar cuenta'}
              </button>
            </form>
          </>
        )}

        {estado === 'activada' && (
          <>
            <h1 className="login-title">¡Listo!</h1>
            <p className="login-subtitle">Tu cuenta quedó activada. Ya podés iniciar sesión con tu nueva contraseña.</p>
            <div className="login-links" style={{ marginTop: 16 }}>
              <Link to="/login" className="link-ayuda">Ir a iniciar sesión</Link>
            </div>
          </>
        )}

        <div className="login-footer">
          <p>
            © 2026 Betech Solutions |
            <a href="https://be-tech.com.ar" target="_blank" rel="noopener noreferrer">
              {' '}www.be-tech.com.ar
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ActivarCuenta;
